import { useMemo, useState } from "react";
import type { FormEvent } from "react";
import { Link } from "react-router-dom";
import PageHero from "@/components/feature/PageHero";
import NewsCard from "@/components/feature/NewsCard";
import { newsCategories, newsList } from "@/mocks/news";
import { teams } from "@/mocks/teams";

const PAGE_SIZE = 4;

function escapeRegExp(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function Highlight({ text, keyword }: { text: string; keyword: string }) {
  const kw = keyword.trim();
  if (!kw) return <>{text}</>;
  const regex = new RegExp(`(${escapeRegExp(kw)})`, "gi");
  const parts = text.split(regex);
  return (
    <>
      {parts.map((part, index) =>
        part.toLowerCase() === kw.toLowerCase() ? (
          <mark
            key={index}
            className="bg-accent-200 text-accent-950 rounded px-0.5"
          >
            {part}
          </mark>
        ) : (
          <span key={index}>{part}</span>
        ),
      )}
    </>
  );
}

export default function NewsList() {
  const [keyword, setKeyword] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [category, setCategory] = useState("全部");
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    return newsList.filter((n) => {
      if (category !== "全部" && n.category !== category) return false;
      if (!kw) return true;
      return (
        n.title.toLowerCase().includes(kw) ||
        n.summary.toLowerCase().includes(kw) ||
        n.author.toLowerCase().includes(kw) ||
        n.tags.some((t) => t.toLowerCase().includes(kw))
      );
    });
  }, [keyword, category]);

  const matchedTeams = useMemo(() => {
    const kw = keyword.trim();
    if (!kw) return [];
    return teams.filter(
      (t) =>
        t.name.includes(kw) ||
        t.englishName.toLowerCase().includes(kw.toLowerCase()) ||
        t.city.includes(kw),
    );
  }, [keyword]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paged = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const submitSearch = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setKeyword(inputValue);
    setPage(1);
  };

  return (
    <div className="w-full">
      <PageHero
        eyebrow="NEWS CENTER"
        title="欧冠资讯中心"
        description="赛事战报、球队动态、转会消息与数据分析，多维度追踪欧冠赛场内外的最新动向。"
        crumbs={[{ label: "首页", to: "/" }, { label: "资讯新闻" }]}
      />

      <section className="w-full px-4 md:px-6 py-10 md:py-14">
        <form onSubmit={submitSearch} className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <i
              className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-base"
              aria-hidden="true"
            ></i>
            <input
              type="text"
              name="news_search"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder="搜索新闻标题、标签或球队名称"
              className="w-full rounded-md border border-background-200 bg-background-50 pl-10 pr-4 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200"
            />
          </div>
          <button
            type="submit"
            className="inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 px-6 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            <i className="ri-search-line" aria-hidden="true"></i>
            搜索
          </button>
          {keyword && (
            <button
              type="button"
              onClick={() => {
                setKeyword("");
                setInputValue("");
                setPage(1);
              }}
              className="inline-flex items-center justify-center gap-2 rounded-md border border-background-200 px-5 py-2.5 text-sm font-semibold text-foreground-600 hover:bg-background-100 transition-colors whitespace-nowrap"
            >
              <i className="ri-close-line" aria-hidden="true"></i>
              清除
            </button>
          )}
        </form>

        <div className="flex flex-wrap items-center gap-2 mb-6">
          {newsCategories.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => {
                setCategory(c);
                setPage(1);
              }}
              className={`px-3.5 py-1.5 rounded-full text-xs md:text-sm font-medium transition-colors whitespace-nowrap ${
                c === category
                  ? "bg-accent-500 text-background-50"
                  : "bg-background-100 text-foreground-600 hover:bg-background-200"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        {keyword && (
          <p className="text-sm text-foreground-600 mb-6">
            关于
            <strong className="mx-1 text-primary-700">
              <Highlight text={keyword} keyword={keyword} />
            </strong>
            共找到 <strong className="text-foreground-900">{filtered.length}</strong> 条新闻
            {matchedTeams.length > 0 && (
              <>
                ，<strong className="text-foreground-900">{matchedTeams.length}</strong> 支球队
              </>
            )}
          </p>
        )}

        {matchedTeams.length > 0 && (
          <div className="mb-8 rounded-lg border border-background-200 bg-background-100 p-5">
            <h2 className="font-heading text-lg font-semibold text-foreground-950 mb-3">
              匹配的球队
            </h2>
            <div className="flex flex-wrap gap-2">
              {matchedTeams.map((t) => (
                <Link
                  key={t.id}
                  to={`/team/${t.id}`}
                  className="inline-flex items-center gap-2 rounded-full border border-background-200 bg-background-50 pl-1.5 pr-4 py-1.5 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-700 transition-colors"
                >
                  <span className="w-7 h-7 rounded-full bg-background-100 flex items-center justify-center overflow-hidden p-0.5">
                    <img
                      src={t.crest}
                      alt={`${t.name}队徽`}
                      title={`${t.name} 欧冠球队`}
                      className="w-full h-full object-contain"
                    />
                  </span>
                  <Highlight text={t.name} keyword={keyword} />
                </Link>
              ))}
            </div>
          </div>
        )}

        {paged.length === 0 ? (
          <div className="rounded-lg border border-dashed border-background-300 py-16 text-center">
            <span className="inline-flex w-14 h-14 items-center justify-center rounded-full bg-background-100 text-2xl text-foreground-400">
              <i className="ri-newspaper-line" aria-hidden="true"></i>
            </span>
            <p className="mt-4 text-sm text-foreground-500">暂无相关新闻</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {paged.map((n) => (
              <NewsCard key={n.id} news={n} />
            ))}
          </div>
        )}

        {totalPages > 1 && (
          <div className="mt-8 flex items-center justify-center gap-2">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={safePage === 1}
              className="w-9 h-9 flex items-center justify-center rounded-md border border-background-200 text-foreground-600 hover:bg-background-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              aria-label="上一页"
            >
              <i className="ri-arrow-left-s-line" aria-hidden="true"></i>
            </button>
            {Array.from({ length: totalPages }).map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setPage(i + 1)}
                className={`w-9 h-9 flex items-center justify-center rounded-md text-sm font-medium transition-colors ${
                  safePage === i + 1
                    ? "bg-primary-500 text-background-50"
                    : "border border-background-200 text-foreground-600 hover:bg-background-100"
                }`}
              >
                {i + 1}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={safePage === totalPages}
              className="w-9 h-9 flex items-center justify-center rounded-md border border-background-200 text-foreground-600 hover:bg-background-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              aria-label="下一页"
            >
              <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
            </button>
          </div>
        )}
      </section>
    </div>
  );
}